<template>
	<div id="app">
		<div v-if="!requiredResourcesAreReady || redirectInPending" class="loader">
			<div class="db-spinner"></div>
		</div>
		<FunnelContainer v-if="requiredResourcesAreReady">
			<ThemeProvider>
				<router-view />
				<LanguageModal />
			</ThemeProvider>
		</FunnelContainer>
	</div>
</template>

<script>
import { defineFunnelByParams } from '@/funnels/funnels.ts'
import { mapActions, mapState } from 'pinia'
import { useMainStore } from '@/store/main'
import { useCoreFeaturesStore } from '@/store/coreFeatures'
import { useI18nStore } from '@/store/i18n'
import { useLocationStore } from '@/store/location'
import { useStatusStore } from '@/store/status'
import { useSurveyStore } from '@/store/survey'
import { useUserFlagsStore } from '@/store/userFlags'

import config from '@/config'
import { getHostnameFromURL, getQueryParamsFromUrl } from '@/helpers/url'
import { filterInvalidKeys } from '@/helpers/utils'
import Jet from '@/modules/Jet'
import Sentry from '@/modules/Sentry'

import { useABFeatureFlag } from '@/composables/useABFeatureFlag'
import { useExperiments } from '@/composables/useExperiments'
import { useOnetrust } from '@/composables/useOnetrust'

import FunnelContainer from '@/components/FunnelContainer.vue'
import ThemeProvider from '@/components/ThemeProvider.vue'
import LanguageModal from '@/components/LanguageModal.vue'
import { hashSha256 } from '@/helpers/hash'
import { loadLanguageAsync, i18n } from '@/i18n'

export default {
	name: 'App',

	components: {
		FunnelContainer,
		ThemeProvider,
		LanguageModal,
	},

	setup() {
		useExperiments()
		useOnetrust()
		const {
			IS_SCONE_ADAPTED_COPY_GB_EXP_B,
			IS_SCONE_ADAPTED_COPY_GB_EXP_C,
			IS_FEMALE_TAI_CHI_WALKING_V2_FR_EXP,
			IS_FEMALE_TAI_CHI_WALKING_V2_DE_EXP,
		} = useABFeatureFlag()
		return {
			IS_SCONE_ADAPTED_COPY_GB_EXP_B,
			IS_SCONE_ADAPTED_COPY_GB_EXP_C,
			IS_FEMALE_TAI_CHI_WALKING_V2_FR_EXP,
			IS_FEMALE_TAI_CHI_WALKING_V2_DE_EXP,
		}
	},

	data: () => ({
		routeQueryParams: {},
	}),

	computed: {
		...mapState(useMainStore, {
			countryCode: 'getCountryCode',
			countryCodeUppercase: 'getCountryCodeUppercase',
			gdpr: 'getGdprStatus',
			userId: 'getUserId',
			currency: 'getCurrency',
			currencyUppercase: 'getCurrencyUppercase',
		}),

		...mapState(useStatusStore, ['requiredResourcesAreReady']),
		...mapState(useLocationStore, {
			getQueryParam: 'getQueryParam',
			isEmptyInitialUtmParams: 'isEmptyInitialUtmParams',
			appAreaConfig: 'getAppAreaConfig',
			initialPath: 'getInitialPath',
			paywallRoute: 'getPaywallRoute',
			hostname: 'hostname',
			getPathname: 'getPathname',
		}),

		...mapState(useCoreFeaturesStore, {
			paltaWhiteList: 'FEAT_PALTA_WHITE_LIST',
			paltaBlackList: 'FEAT_PALTA_BLACK_LIST',
		}),

		...mapState(useI18nStore, {
			language: 'getLanguage',
		}),

		...mapState(useMainStore, ['marketingDeduplicationEventId']),

		...mapState(useLocationStore, {
			initialUtmParams: 'initialUtmParams',
		}),

		redirectInPending() {
			return useStatusStore().resourceIsPending('redirect')
		},
	},

	watch: {
		requiredResourcesAreReady: {
			handler(value) {
				if (!value) {
					return
				}

				// web_scone_adapted_copy_gb
				if (this.language === 'en') {
					if (this.IS_SCONE_ADAPTED_COPY_GB_EXP_B) {
						loadLanguageAsync({ i18nLocaleOverride: 'en-GB' })
					} else if (this.IS_SCONE_ADAPTED_COPY_GB_EXP_C) {
						loadLanguageAsync({ i18nLocaleOverride: 'en-gb-ai' })
					}
				}

				if (this.IS_FEMALE_TAI_CHI_WALKING_V2_FR_EXP) {
					i18n.mergeLocaleMessage('fr', {
						pages: {
							landing: {
								tai_chi: {
									loader_title: 'Perdez du poids grâce à la marche taï-chi selon votre âge',
									age_title: 'Perdez du poids grâce à la marche taï-chi',
								},
							},
							wls_chart: {
								now: "AUJOURD'HUI",
								note: {
									toned: 'et vous aurez le corps musclé dont vous rêvez',
								},
							},
							result: {
								loaders: {
									meals_and_activity: "Préférences en matière d'activité physique",
								},
							},
							personalize_plan: {
								features: {
									workouts: 'Perdez du poids et musclez-vous avec des exercices doux, mais efficaces',
									no_equipment: 'Des séances brûle-graisse, sans matériel',
									nutrition: 'Des conseils nutritionnels pour de meilleurs résultats',
									assistant: 'Un assistant bien-être personnalisé 24 h/24, 7 j/7',
								},
							},
							signup: {
								consent_button_no: 'Non merci',
							},
						},
						landing_male: {
							loader: {
								label: 'Encore quelques instants…',
								note: 'Nous analysons votre profil…',
							},
						},
						female_activity: {
							pages: {
								landing: {
									step_1: {
										subtitle: 'SELON VOTRE <b>ÂGE</b>',
									},
								},
							},
						},
						female_yoga: {
							pages: {
								landing: {
									age_range: {
										'40_49': 'De 40 à 49 ans',
										'50_59': 'De 50 à 59 ans',
										'60_69': 'De 60 à 69 ans',
										'70_80': 'De 70 à 80 ans',
									},
								},
							},
						},
						health_data_consent: {
							checkbox_text:
								"J'accepte que Simple traite mes données de santé (telles que ma taille et mon poids) pour me fournir des services, conformément à la {privacy_policy}",
							privacy_policy: 'Politique de confidentialité.',
						},
						tai_chi_walking_f_v2: {
							paywall: {
								enter_your_email: 'Indiquez votre e-mail pour recevoir votre programme de marche taï-chi',
							},
						},
					})
				}

				if (this.IS_FEMALE_TAI_CHI_WALKING_V2_DE_EXP) {
					i18n.mergeLocaleMessage('de', {
						common: {
							controls: {
								see_my_plan: 'Meinen Plan erhalten',
							},
						},
						pages: {
							landing: {
								tai_chi: {
									loader_title: 'Nimm ab mit Tai-Chi-Walking, angepasst an dein Alter',
									age_title: 'Nimm ab mit Tai-Chi-Walking',
								},
							},
							wls_chart: {
								note: {
									curvy: 'Und bekommst den straffen Körper, den du dir wünschst',
								},
								goal: 'Zielgewicht',
							},
							result: {
								analyzing_your_answers: 'Wir analysieren deine Antworten',
								loaders: {
									meals_and_activity: 'Ernährung und Aktivitäten',
									generating_personalized_plan: 'Dein Plan wird erstellt',
								},
							},
							personalize_plan: {
								cta: 'Plan erhalten',
							},
							signup: {
								gdpr_title_v2: 'Möchtest du Abnehmtipps, Rezepte und Angebote von Simple erhalten?',
								consent_button_yes: 'Ja, gerne',
							},
						},
						landing_male: {
							loader: {
								label: 'Einen Augenblick bitte …',
								note: 'Wir analysieren dein Profil …',
							},
							results_disclaimer:
								'Die Ergebnisse hängen von deinem Ausgangspunkt, deinen Zielen und deinem Ehrgeiz ab. Simple kann dich nicht medizinisch beraten.',
						},
						female_activity: {
							pages: {
								landing: {
									step_1: {
										subtitle: 'Angepasst an dein Alter',
									},
								},
							},
						},
						female_yoga: {
							pages: {
								landing: {
									im_young: 'Ich bin 18–39 Jahre alt',
								},
								wls_chart: {
									members_like_you: 'Ermittelt anhand von Simple Mitgliedern mit ähnlichen Zielen',
								},
							},
						},

						tai_chi_walking_f_v2: {
							paywall: {
								enter_your_email: 'Gib deine E-Mail-Adresse ein, um deinen Tai-Chi-Walking-Plan zu erhalten',
							},
						},

						'female-funnel': {
							pages: {
								wls_chart: {
									note: '* Basiert auf Simple Mitgliedern mit einem ähnlichen Ziel.',
								},
							},
						},
					})
				}

				// После загрузки geodata надо переопределить appArea чтобы применились overrides по country
				const appAreaConfig = defineFunnelByParams(
					{
						appArea: this.getQueryParam('area') ?? null,
						initialPath: this.initialPath,
						country: this.countryCode,
						domain: this.hostname,
					},
					this.$absmartly,
				)

				useLocationStore().setAppAreaConfig(appAreaConfig)

				this.initAnalyticDestinationPlugins()
				this.setAnalyticsContext()
				this.processUtmParams()
				useLocationStore().defineOrganicTrafficFlag()
			},
		},

		'appAreaConfig.appArea': {
			handler(newValue, oldValue) {
				if (newValue === oldValue) {
					return
				}

				this.$analytic.setUserProperties({
					FunnelId: this.appAreaConfig.funnel.id,
					PaywallRoute: this.paywallRoute,
				})

				Jet.setOnboarding({
					onboardingFunnelId: this.appAreaConfig.funnel.id,
					onboardingFunnelType: this.appAreaConfig.funnel.slug,
					onboardingUrlPath: this.$route.path,
				})

				Sentry.setTag('funnelId', this.appAreaConfig.funnel.id)
				Sentry.setTag('funnelType', this.appAreaConfig.funnel.slug)
				Sentry.setTag('countryCode', this.countryCodeUppercase)
				Sentry.setTag('currencyCode', this.currencyUppercase)
			},

			immediate: true,
		},
	},

	async created() {
		const queryParams = getQueryParamsFromUrl(window.location.href)
		this.routeQueryParams = {
			referrer: getHostnameFromURL(document.referrer),
			url: window.location.href,
			...queryParams,
		}

		if (window.language === 'ar') {
			import(/* webpackChunkName: "rtl-styles" */ '@/styles/rtl.scss')
		}

		useLocationStore().storeQueryParams()
		let routeWithParamsSync = this.$router.resolve(`${this.getPathname()}${window.location.search}`)

		// Vue Router 4: resolve() does not follow redirects (unlike Vue Router 3).
		// Manually follow redirect to get the target route's path and meta.
		const redirectTarget = routeWithParamsSync.matched[0]?.redirect
		if (redirectTarget) {
			if (typeof redirectTarget === 'string') {
				routeWithParamsSync = this.$router.resolve(`${redirectTarget}${window.location.search}`)
			} else {
				// Named or object redirect — resolve via router to get the correct path
				const resolved = this.$router.resolve(redirectTarget)
				routeWithParamsSync = this.$router.resolve(`${resolved.path}${window.location.search}`)
			}
		}

		const isRefreshSession = Boolean(
			routeWithParamsSync.meta.isLanding &&
				(routeWithParamsSync.query.utm_source || routeWithParamsSync.redirectedFrom),
		)

		await useLocationStore().storeInitialPath({
			force: isRefreshSession,
			path: routeWithParamsSync.path,
		})

		if (isRefreshSession) {
			// We store initial funnel type to be able to track CRs for gender selection funnels
			useLocationStore().setInitialFunnelType(this.appAreaConfig.funnel.slug)
		}

		this.setParamsToGTMDataLayer({ isLanding: routeWithParamsSync.meta.isLanding })
	},

	methods: {
		...mapActions(useSurveyStore, ['addUserConsent', 'removeUserConsent']),
		...mapActions(useUserFlagsStore, ['updateLocalFlags']),

		setParamsToGTMDataLayer({ isLanding }) {
			window.dataLayer?.push({
				event_id: this.marketingDeduplicationEventId,
			})
			window.dataLayer?.push({
				event: 'updateGtagData',
				country_code_sha256: hashSha256(this.countryCode?.toUpperCase()),
				tracking_enabled: !Boolean(isLanding),
			})
		},

		initAnalyticDestinationPlugins() {
			this.$analytic.initDestinationPlugins([
				{
					instanceOptions: {
						apiKey: config('AmplitudePaltaWeb'),
						serverUrl: 'https://telemetry.dataplatform.simple.life/v2/amplitude',
					},

					whiteList: this.paltaWhiteList,
					blackList: this.paltaBlackList,
				},
			])
		},

		setAnalyticsContext() {
			this.$analytic.setUserProperties({
				countryCode: this.countryCode,
				currency: this.currency,
				gdpr: this.gdpr,
				AppArea: this.appAreaConfig.appArea,
			})

			const appContext = filterInvalidKeys({
				appCurrency: this.currency,
				appGdprEnabled: this.gdpr,
			})

			const deviceContext = filterInvalidKeys({
				deviceCountry: this.countryCode,
			})

			Jet.setApplication(appContext)
			Jet.setDevice(deviceContext)
		},

		processUtmParams() {
			if (!this.routeQueryParams.utm_source) {
				// Send this event only if any page is being loaded with `utm_source` parameter in the URL
				return
			}

			const referrer = getHostnameFromURL(document.referrer)
			const commonParams = {
				referrer: referrer,
				url: window.location.href,
				fbclid: this.routeQueryParams.fbclid,
				gclid: this.routeQueryParams.gclid,
			}

			window.dataLayer?.push({
				event: 'updateGtagData',
				utm_source: this.routeQueryParams.utm_source,
				utm_medium: this.routeQueryParams.utm_medium,
				utm_campaign: this.routeQueryParams.utm_campaign,
				utm_term: this.routeQueryParams.utm_term,
				utm_content: this.routeQueryParams.utm_content,
			})

			if (this.isEmptyInitialUtmParams) {
				useLocationStore().storeInitialUtmParams({
					initial_referrer: referrer,
					initial_utm_adgroup_id: this.routeQueryParams.adgroup_id,
					initial_utm_ad_id: this.routeQueryParams.ad_id,
					initial_utm_campaign_id: this.routeQueryParams.campaign_id,
					initial_utm_medium: this.routeQueryParams.utm_medium,
					initial_utm_source: this.routeQueryParams.utm_source,
					initial_utm_campaign: this.routeQueryParams.utm_campaign,
					initial_utm_content: this.routeQueryParams.utm_content,
					initial_utm_term: this.routeQueryParams.utm_term,
				})
			}

			this.$analytic.logEvent(
				'UtmParamsParsingDone',
				filterInvalidKeys({
					...commonParams,
					adgroup_id: this.routeQueryParams.adgroup_id,
					ad_id: this.routeQueryParams.ad_id,
					campaign_id: this.routeQueryParams.campaign_id,
					utm_gender: this.routeQueryParams.utm_gender,
					utm_medium: this.routeQueryParams.utm_medium,
					utm_source: this.routeQueryParams.utm_source,
					utm_campaign: this.routeQueryParams.utm_campaign,
					utm_content: this.routeQueryParams.utm_content,
					utm_term: this.routeQueryParams.utm_term,
					initial_referrer: this.initialUtmParams.initial_referrer,
					initial_utm_adgroup_id: this.initialUtmParams.initial_utm_adgroup_id,
					initial_utm_ad_id: this.initialUtmParams.initial_utm_ad_id,
					initial_utm_campaign_id: this.initialUtmParams.initial_utm_campaign_id,
					initial_utm_medium: this.initialUtmParams.initial_utm_medium,
					initial_utm_source: this.initialUtmParams.initial_utm_source,
					initial_utm_campaign: this.initialUtmParams.initial_utm_campaign,
					initial_utm_content: this.initialUtmParams.initial_utm_content,
					initial_utm_term: this.initialUtmParams.initial_utm_term,
				}),
			)

			Jet.logLogicalEvent(
				'UtmParamsParsingDone',
				filterInvalidKeys({
					...commonParams,
					adgroupId: this.routeQueryParams.adgroup_id,
					adId: this.routeQueryParams.ad_id,
					campaignId: this.routeQueryParams.campaign_id,
					utmGender: this.routeQueryParams.utm_gender,
					utmMedium: this.routeQueryParams.utm_medium,
					utmSource: this.routeQueryParams.utm_source,
					utmCampaign: this.routeQueryParams.utm_campaign,
					utmContent: this.routeQueryParams.utm_content,
					utmTerm: this.routeQueryParams.utm_term,
					initialReferrer: this.initialUtmParams.initial_referrer,
					initialUtmAdgroupId: this.initialUtmParams.initial_utm_adgroup_id,
					initialUtmAdId: this.initialUtmParams.initial_utm_ad_id,
					initialUtmCampaignId: this.initialUtmParams.initial_utm_campaign_id,
					initialUtmMedium: this.initialUtmParams.initial_utm_medium,
					initialUtmSource: this.initialUtmParams.initial_utm_source,
					initialUtmCampaign: this.initialUtmParams.initial_utm_campaign,
					initialUtmContent: this.initialUtmParams.initial_utm_content,
					initialUtmTerm: this.initialUtmParams.initial_utm_term,
				}),
			)
		},
	},
}
</script>

<style lang="scss">
@import '@/styles/style.scss';

#app {
	-webkit-font-smoothing: antialiased;
	-moz-osx-font-smoothing: grayscale;
}

.loader {
	position: fixed;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	background-color: var(--background-color);
	z-index: 9;
	display: flex;
	justify-content: center;
	align-items: center;
}
</style>
